import { useTranslation } from "react-i18next";
import { MOVEMENT_KINDS, accountLabel, type ExpenseAccount, type PeriodClash, type Reconciliation } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { money } from "../lib/format";
import { ui } from "../theme";
import { Chip, Field, Kpi, Notice } from "./ui";

// Cuenta, periodo y total declarado de una importación + su cuadre (#952 / #954).

const NS = "expensesStatements";

export interface ImportMeta {
  accountId: string;
  from: string;
  to: string;
  declared: string;
}

export const RECONCILE_TONES: Record<Reconciliation["status"], string> = {
  por_conciliar: "bg-amber-500/15 text-amber-600",
  conciliado: "bg-emerald-500/15 text-emerald-600",
  con_diferencia: "bg-red-500/15 text-red-600",
};

export const parseDeclared = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};

export default function ReconcilePanel({
  meta,
  onChange,
  accounts,
  suggested,
  reconciliation,
  clash,
  confirmed,
  onSave,
  saving,
}: {
  meta: ImportMeta;
  onChange: (m: ImportMeta) => void;
  accounts: ExpenseAccount[];
  suggested?: boolean;
  reconciliation: Reconciliation;
  clash: PeriodClash;
  confirmed: boolean;
  onSave?: () => void;
  saving?: boolean;
}) {
  const { t, i18n } = useTranslation(["common", NS]);
  const tt = (k: string, o?: Record<string, unknown>) => t(`${NS}:${k}`, o);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const cur = reconciliation.currency;
  const r = reconciliation;
  const bad = r.status === "con_diferencia" || (r.difference != null && Math.abs(r.difference) > 0.01);

  return (
    <div className={`${c.card} space-y-3`} data-testid="reconcile-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={`text-sm font-semibold ${c.text}`}>{tt("reconcile.title")}</h3>
        <Chip className={RECONCILE_TONES[r.status]}>{tt(`reconcile.status.${r.status}`)}</Chip>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Field label={tt("import.account")}>
          <select aria-label="import-account" className={`${c.input} ${suggested ? "border-amber-500" : ""}`} value={meta.accountId} onChange={(e) => onChange({ ...meta, accountId: e.target.value })}>
            <option value="">{tt("common.noAccount")}</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {accountLabel(a)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tt("import.from")}>
          <input type="date" aria-label="meta-from" className={c.input} value={meta.from} onChange={(e) => onChange({ ...meta, from: e.target.value })} />
        </Field>
        <Field label={tt("import.to")}>
          <input type="date" aria-label="meta-to" className={c.input} value={meta.to} onChange={(e) => onChange({ ...meta, to: e.target.value })} />
        </Field>
        <Field label={tt("import.declaredTotal")}>
          <input aria-label="meta-declared" inputMode="decimal" className={c.input} value={meta.declared} onChange={(e) => onChange({ ...meta, declared: e.target.value })} placeholder={tt("import.declaredHint")} />
        </Field>
      </div>
      {suggested && meta.accountId && <p className="text-xs text-amber-600">{tt("import.accountSuggested")}</p>}
      {clash.exact && <Notice tone="warn">{tt("reconcile.clash")}</Notice>}
      {!clash.exact && clash.overlaps.length > 0 && <Notice>{tt("reconcile.overlaps")}</Notice>}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label={tt("reconcile.declared")} value={r.declared == null ? tt("reconcile.notDeclared") : money(r.declared, cur, i18n.language)} />
        <Kpi label={tt("reconcile.imported")} value={money(r.imported, cur, i18n.language)} />
        <Kpi label={tt("reconcile.credits")} value={money(r.credits, cur, i18n.language)} />
        <Kpi label={tt("reconcile.difference")} value={r.difference == null ? "—" : money(r.difference, cur, i18n.language)} tone={r.difference == null ? undefined : bad ? "bad" : "good"} />
      </div>
      <div className="flex flex-wrap gap-2 text-xs" aria-label="by-kind">
        {MOVEMENT_KINDS.filter((k) => r.byKind[k].count > 0).map((k) => (
          <span key={k} className={`rounded-full border px-2 py-0.5 ${c.border} ${c.muted}`}>
            {tt(`import.kinds.${k}`)}: {r.byKind[k].count} · {money(r.byKind[k].total, cur, i18n.language)}
          </span>
        ))}
      </div>
      {r.otherCurrency > 0 && <p className={`text-xs ${c.muted}`}>{tt("reconcile.otherCurrency", { count: r.otherCurrency })}</p>}
      {confirmed && onSave && (
        <div className="flex justify-end">
          <button type="button" className={c.secondary} disabled={saving || Boolean(clash.exact)} onClick={onSave}>
            {tt("reconcile.saveMeta")}
          </button>
        </div>
      )}
    </div>
  );
}
